import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, LinearProgress } from '@mui/material';
import './TotalQuantityDialog.css';

/**
 * TotalQuantityDialog Component
 * 
 * Displays a modal dialog showing the total quantity when it exceeds the maximum limit.
 * Only appears when total quantity > maxQuantity.
 * User must go back and modify quantities - no option to continue.
 * 
 * @param {boolean} open - Controls whether the dialog is open
 * @param {Function} onClose - Callback when dialog is closed
 * @param {Object} formData - The complete form data containing all test data items
 * @param {string} category - Either 'postpaid' or 'prepaid'
 * @param {number} maxQuantity - Maximum allowed quantity (default: 100)
 */
const TotalQuantityDialog = ({ 
  open,
  onClose,
  formData, 
  category, 
  maxQuantity = 100
}) => {
  const [totalQuantity, setTotalQuantity] = useState(0);

  // Calculate total quantity when dialog opens
  useEffect(() => {
    if (open) {
      const newTotal = calculateTotalQuantity(formData, category);
      setTotalQuantity(newTotal);
    }
  }, [open, formData, category]);

  const { background, text } = getStatusColor(totalQuantity, maxQuantity);
  const percentage = (totalQuantity / maxQuantity) * 100;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
            animation: 'popOut 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Total Quantity Summary
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box
          sx={{
            padding: '16px',
            borderRadius: '8px',
            backgroundColor: background,
            color: text,
            textAlign: 'center',
            mb: 2,
          }}
        >
          <Typography variant="caption" sx={{ mb: 1, opacity: 0.9, fontSize: '0.75rem', display: 'block' }}>
            Total Quantity
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              mb: 1.5,
              fontSize: '2.5rem',
              lineHeight: 1,
              animation: 'numberPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s backwards',
            }}
          >
            {totalQuantity}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.3)',
              mb: 1,
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#ffffff',
                borderRadius: 3,
              },
            }}
          />

          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {percentage.toFixed(0)}% of {maxQuantity} units
          </Typography>

          <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 'bold' }}>
            ⚠️ Limit Exceeded!
          </Typography>
        </Box>

        <Typography variant="body2" color="error" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          Your total quantity exceeds {maxQuantity} units. Please go back and reduce your quantities to {maxQuantity} units or less. Note: More than 100 needs CDM to approve via email. Or submit manually via synphony summit.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          autoFocus
        >
          Go Back and Modify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Calculate total quantity from form data based on category
 * 
 * @param {Object} formData - The complete form data
 * @param {string} category - Either 'postpaid' or 'prepaid'
 * @returns {number} Total quantity across all items
 */
const calculateTotalQuantity = (formData, category) => {
  let total = 0;

  if (!formData) return 0;

  if (category === 'postpaid') {
    const postpaidData = formData.Postpaid || {};
    
    // Sum quantities from all postpaid item types
    ['MSISDN', 'Blank Sim', 'eSIM', 'VOBB'].forEach(itemType => {
      const item = postpaidData[itemType];
      if (item && item.quantity) {
        const quantity = String(item.quantity).trim();
        if (quantity !== '') {
          const num = Number(quantity);
          // Only add if it's a valid positive integer
          if (!Number.isNaN(num) && Number.isInteger(num) && num > 0) {
            total += num;
          }
        }
      }
    });
  } else if (category === 'prepaid') {
    // Process all prepaid sections
    const sections = ['Prepaid', 'Starter Pack', 'Softpin', 'Reload'];
    
    sections.forEach(section => {
      const sectionData = formData[section] || {};
      
      // Iterate through all items in each section
      Object.values(sectionData).forEach(item => {
        if (item && typeof item === 'object' && item.quantity) {
          const quantity = String(item.quantity).trim();
          if (quantity !== '') {
            const num = Number(quantity);
            // Only add if it's a valid positive integer
            if (!Number.isNaN(num) && Number.isInteger(num) && num > 0) {
              total += num;
            }
          }
        }
      });
    });
  }

  return total;
};

/**
 * Determine status color - always red since dialog only shows when exceeded
 * 
 * @param {number} totalQuantity - Current total quantity
 * @param {number} maxQuantity - Maximum allowed quantity
 * @returns {Object} Object containing background color, text color, and status
 */
const getStatusColor = (totalQuantity, maxQuantity) => {
  // Always red since dialog only appears when quantity > maxQuantity
  return {
    background: '#f44336', // Red
    text: '#ffffff',
    status: 'exceeded',
  };
};

export default TotalQuantityDialog;
